/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { getProductPageUrlSync } from '@msdyn365-commerce-modules/retail-actions';
import { INodeProps, Module, Node } from '@msdyn365-commerce-modules/utilities';
import { IPriceComponentResources, PriceComponent } from '@msdyn365-commerce/components';
import { ICoreContext, Image } from '@msdyn365-commerce/core';
import { ProductPrice } from '@msdyn365-commerce/retail-proxy';
import * as React from 'react';
import { getImageSettings, IProductCollectionViewProps, IProductComponentViewProps } from './cws-product-recommendation';
import { ICwsProductRecommendationResources } from './cws-product-recommendation.props.autogenerated';

const ProductCollectionView: React.FC<IProductCollectionViewProps> = props => {
    const shouldDisablePrices = Boolean(props.config.disablePrices);

    const {
        heading,
        ProductCollectionContainer,
        products,
        SingleSlideCarouselComponentProps,
        seeAllButton,
        resources,
        context,
        id,
        typeName
    } = props;

    if (products) {
        return (
            <Module {...ProductCollectionContainer}>
                {heading}
                {seeAllButton}
                {_renderRecommandationsCarousel(SingleSlideCarouselComponentProps, products, context, id, typeName, shouldDisablePrices)}
            </Module>
        );
    }

    // If no products are retrieved and the list type is promotional, still render the module to keep the entry of promotion page
    if (props.config.productCollection.listType === 'promotional') {
        return (
            <Module {...ProductCollectionContainer}>
                {heading}
                {seeAllButton}
                <Node className='cws-product-recommendation'>{_renderNoProduct(resources)}</Node>
            </Module>
        );
    }
    props.context.telemetry.error('Product collection content is empty, module wont render.');
    return null;
};
const _renderRecommandationsCarousel = (
    carouselContainer: INodeProps,
    items: IProductComponentViewProps[],
    context: ICoreContext,
    id: string,
    typeName: string,
    disablePrices: boolean
): JSX.Element => {
    return (
        <Node {...carouselContainer}>
            {items && items.map(item => _renderProductRecommandations(item, context, id, typeName, disablePrices))}
        </Node>
    );
};

const _renderNoProduct = (resources: ICwsProductRecommendationResources): JSX.Element => {
    return (
        <Node className={''}>
            <p>{resources.noProductToRenderText}</p>
        </Node>
    );
};

const _renderProductRecommandations = (
    product: IProductComponentViewProps,
    context: ICoreContext,
    id: string,
    typeName: string,
    disablePrices: boolean
): JSX.Element => {
    const { ProductContainer, productComponent } = product;
    const simpleProduct = productComponent as any;

    const productUrl = product
        ? getProductPageUrlSync(
              simpleProduct.props.data.product.Name ?? '',
              simpleProduct.props.data.product.RecordId,
              context.actionContext,
              undefined
          )
        : '';

    function renderPrice(
        context: ICoreContext,
        typeName: string,
        id: string,
        basePrice?: number,
        adjustedPrice?: number,
        savingsText?: string,
        freePriceText?: string,
        originalPriceText?: string,
        isPriceMinMaxEnabled?: boolean,
        priceResources?: IPriceComponentResources
    ): JSX.Element | null {
        const price: ProductPrice = {
            BasePrice: basePrice,
            AdjustedPrice: adjustedPrice,
            CustomerContextualPrice: adjustedPrice
        };

        return (
            <PriceComponent
                context={context}
                id={id}
                typeName={typeName}
                data={{ price }}
                savingsText={savingsText}
                freePriceText={freePriceText}
                originalPriceText={originalPriceText}
                isPriceMinMaxEnabled={isPriceMinMaxEnabled}
                priceResources={priceResources}
            />
        );
    }

    return (
        <Node {...ProductContainer}>
            <div className='msc-product'>
                <a className='msc-product__image' href={productUrl} aria-label={simpleProduct.props.data.product.Name}>
                    <Image
                        src={simpleProduct.props.data.product.PrimaryImageUrl ?? ''}
                        altText={simpleProduct.props.data.product.Name ?? ''}
                        gridSettings={context.request.gridSettings!}
                        imageSettings={getImageSettings()}
                        loadFailureBehavior='empty'
                        className='msc-main_image'
                    />
                </a>
                <div className='msc-product__details'>
                    <h5 className='msc-product__title'>{simpleProduct.props.data.product.Name}</h5>
                    {!disablePrices &&
                        renderPrice(
                            context,
                            typeName,
                            id,
                            simpleProduct.props.data.product.BasePrice,
                            simpleProduct.props.data.product.Price
                        )}
                </div>
            </div>
        </Node>
    );
};

export default ProductCollectionView;
